import React, { FC, memo, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import { UploadVesselManifest } from '@/api/Vessel';
import { useDocumentTypes } from '@/api/DocumentManagement';
import { useSearchParams } from 'react-router-dom';

import { App, Form, Modal, Select, Spin } from 'antd';
import { UploadVesselModalProps } from './types';
import { DragUpload } from '@/ui/components';

export enum VesselEntities {
  Profile = 'Profile',
  Client = 'Client',
}

export const UploadVesselModal: FC<UploadVesselModalProps> = memo(
  ({ isOpen, closeModal }) => {
    const { isFetching, data } = useDocumentTypes();
    const { mutateAsync: uploadDocument } = UploadVesselManifest();
    const { t } = useTranslate();
    const { message } = App.useApp();
    const [, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState(true);
    const [fileBase64, setFileBase64] = useState<string>();
    const [fileName, setFileName] = useState<string>('');
    const [file, setFile] = useState<File>();
    const [documentTypeCode, setDocumentTypeCode] = useState<string>('');
    const [entity, setEntity] = useState<VesselEntities>(VesselEntities.Client);

    useEffect(() => {
      if (!documentTypeCode) {
        setDocumentTypeCode(data?.[0]?.documentTypeCode || '');
      }
    }, [data, documentTypeCode]);

    const handleFileChange = (files: UploadFile[]) => {
      if (files.length === 0) {
        setFile(undefined);
        setFileName('');
        setFileBase64(undefined);
        return;
      }

      const item = files[0].originFileObj as File;
      if (!item) return;

      const reader = new FileReader();
      reader.onload = ({ target }) => {
        if (target?.result) {
          setFile(item);
          setFileName(item.name);
          setFileBase64(target.result as string);
        }
      };
      reader.readAsDataURL(item); // convert to base64 string
    };

    const handleSubmit = async () => {
      const requiredFields = {
        fileName,
        documentFileBase64: fileBase64,
        entity,
      };

      if (Object.values(requiredFields).some((value) => !value)) {
        message.error('All fields are required');
        return;
      }

      const forData = new FormData();
      if (file) {
        forData.append('file', file);
        const res = await uploadDocument(forData);
        if (res.success) {
          message.success('Upload successful!');
        } else {
          message.error(res.message?.Message, 10);
        }
      }

      onCloseModal();
    };

    const onCloseModal = () => {
      setOpen(false);
      setTimeout(() => {
        setOpen(true);
        closeModal();
        // clear all URL Search Params
        setSearchParams({}, { replace: true });
      }, 500);
    };

    return isOpen ? (
      <Modal
        title={t('uploadDocument')}
        open={open}
        onCancel={onCloseModal}
        okText={t('upload')}
        onOk={handleSubmit}
        footer={(_, { OkBtn }) => <OkBtn />}
        width="50vw"
      >
        <Spin spinning={isFetching}>
          <Form size="large" labelCol={{ flex: '0 0 100px' }}>
            <Form.Item label={t('selectFile')}>
              <DragUpload
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                showUploadBtn={false}
                onFileChanged={handleFileChange}
                multiple
              />
            </Form.Item>

            <Form.Item label={t('entity')}>
              <Select
                value={entity}
                onChange={(e) => setEntity(e as VesselEntities)}
                options={Object.keys(VesselEntities).map((key) => ({
                  label: key,
                  value: key,
                }))}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    ) : null;
  },
);
